import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import { motion } from 'framer-motion';
import { GitHub as GitHubIcon, Launch as LaunchIcon, YouTube as YouTubeIcon } from '@mui/icons-material';

const MotionCard = motion.create(Card);

interface Project {
  title: string;
  description: string;
  image: string;
  imageObjectFit?: 'cover' | 'contain';
  technologies: string[];
  githubUrl?: string;
  liveUrl?: string;
  videoUrl?: string;
  published: string;
}

const projects: Project[] = [
  {
    title: 'Jobriq: AI-Powered Career Optimization Platform',
    description: "Jobriq is a full-stack SaaS I designed and built from concept through production: an intelligent job search system that helps candidates evaluate role fit before investing time in resume tailoring. The platform combines a proprietary viability scoring engine, multi-agent generative AI on Amazon Bedrock (Claude Opus, Sonnet, Haiku), and iterative ATS-aligned resume optimization grounded strictly in verified user data. It showcases production-grade AWS architecture: EKS and Kubernetes for the API, RDS PostgreSQL and ElastiCache Redis for data and job queues, Cognito for identity, S3 and CloudFront for the frontend, and SES for automated email lifecycle. Stripe powers the freemium subscription model (Pro at $39/mo), while Terraform and GitHub Actions define infrastructure and CI/CD to staging and production. Beyond the stack, the project demonstrates end-to-end product delivery (pipeline tracking, smart nudges, interview prep with profile-grounded coaching, a STAR achievement wizard, and recruiter-style profile coaching), all built as sole architect and developer. Jobriq is a live product and a concrete example of production SaaS built with generative AI, modern cloud infrastructure, and user-centric design.",
    image: '/images/projects/jobriq.png',
    imageObjectFit: 'contain',
    technologies: [
      'AWS EKS',
      'Kubernetes',
      'Amazon Bedrock',
      'Claude (Opus / Sonnet / Haiku)',
      'React',
      'TypeScript',
      'Vite',
      'Node.js',
      'Fastify',
      'PostgreSQL',
      'Prisma',
      'Redis',
      'BullMQ',
      'AWS Cognito',
      'Stripe',
      'AWS SES',
      'S3',
      'CloudFront',
      'RDS',
      'ElastiCache',
      'Terraform',
      'GitHub Actions',
      'CI/CD',
      'Tailwind CSS',
      'Framer Motion',
      'OIDC',
    ],
    liveUrl: 'https://jobriq.com',
    published: 'Feb 2026',
  },
  {
    title: 'Personal Portfolio Website Architecture',
    description: "This very website you're exploring serves as a living example of enterprise-grade architecture and modern web development practices. Built with a comprehensive tech stack that combines cutting-edge frontend technologies with robust AWS infrastructure, it demonstrates real-world implementation of secure identity management, global content delivery, and automated deployment workflows. The architecture showcases practical experience with AWS Organizations, IAM Identity Center, and Control Tower for enterprise-grade security and governance, while CloudFront and Route 53 ensure optimal global performance. This portfolio isn't just a showcase of other projects and it's a testament to the quality and sophistication of the solutions I build.",
    image: '/images/projects/portfolio-architecture.jpg',
    technologies: [
      // AWS Services
      'AWS Organizations',
      'AWS IAM Identity Center',
      'AWS Control Tower',
      'Cognito',
      'CloudFront',
      'Route 53',
      'ACM (Certificate Manager)',
      'S3',
      'Lambda',
      'DynamoDB',
      'API Gateway',
      // Frontend Technologies
      'React',
      'TypeScript',
      'Material-UI',
      'Responsive Design',
      // DevOps & Automation
      'Google Analytics',
      'Terraform',
      'Cursor AI Code Editor',
      'CI/CD',
      'GitHub Actions'
    ],
    published: 'Current'
  },
  {
    title: 'Enhancing RAG Architecture with Long-Term Memory',
    description: 'Enhancing RAG with long-term memory brings us closer to truly intelligent virtual assistants, ones that remember past conversations, personalize responses, and evolve over time.',
    image: '/images/projects/long-term-memory.jpg',
    technologies: ['RAG Architecture', 'Long-Term Memory', 'Pinecone', 'MongoDB', 'ChatGPT'],
    githubUrl: 'https://github.com/msuliot/rag-ltm-demo.git',
    videoUrl: 'https://youtu.be/ZpushrauEjo',
    published: 'Jun 29, 2024',
  },
  {
    title: 'Enhancing ChatGPT with Long-Term Memory',
    description: 'Just added long-term memory to our chat system so it remembers past conversations across sessions, making every interaction smarter and more personalized.',
    image: '/images/projects/ai-react-demo.jpg',
    technologies: ['Long-Term Memory', 'Short-Term Memory', 'MongoDB', 'Chat System Architecture', 'Conversational AI'],
    githubUrl: 'https://github.com/msuliot/long-term-memory.git',
    videoUrl: 'https://youtu.be/b-t9i_D77Sg',
    published: 'Jun 11, 2024',
  },
  {
    title: 'Building a RAG Architecture: Local Files and Web Data Extraction - Pinecone, Mongo and ChatGPT',
    description: 'Learn how to build a full RAG architecture from data extraction to AI-powered application, using TEXTEN, WEBTEXTEN, Pinecone, MongoDB, and ChatGPT for secure, scalable, and intelligent information retrieval.',
    image: '/images/projects/texten.jpg',
    technologies: ['RAG Architecture', 'TEXTEN', 'WEBTEXTEN', 'Pinecone', 'MongoDB', 'ChatGPT', 'PII Handling', 'Edge Server', 'Vector Database', 'Embeddings'],
    githubUrl: 'https://github.com/msuliot/texten',
    videoUrl: 'https://youtu.be/GabkV8S0g_A',
    published: 'May 20, 2024',
  },
  {
    title: 'Hugging Face - Text to Speech - Getting started in 5 minutes',
    description: 'Learn how to leverage HuggingFace\'s text-to-speech models using Transformers and local implementation, with practical coding tips and best practices for efficient, responsible AI use.',
    image: '/images/projects/webtexten.jpg',
    technologies: ['HuggingFace', 'Text-to-Speech', 'Transformers', 'Python', 'AI Model Integration', 'Model Card', 'Audio AI', 'NLP', 'Wave File Generation', 'Local Inference'],
    githubUrl: 'https://github.com/msuliot/huggingface_text_to_speech.git',
    videoUrl: 'https://youtu.be/47hba0If7dY',
    published: 'Aug 6, 2023',
  },
  {
    title: 'Build a fully functional chatbot powered by a fine-tuned ChatGPT 3.5 model, integrated with a Python API and React frontend to deliver responses tailored to your custom data.',
    description: 'A specialized node for RAG systems that handles text chunking and processing, optimizing content for AI retrieval and generation tasks.',
    image: '/images/projects/chunken.jpg',
    technologies: ['ChatGPT Fine-Tuning', 'OpenAI API', 'Python Flask', 'React', 'Custom Chatbot', 'AI Personalization', 'JSONL Training Data', 'Node.js', 'DigitalOcean', 'Frontend-Backend Integration'],
    githubUrl: 'https://github.com/msuliot/ai-react-demo',
    videoUrl: 'https://youtu.be/RkDeIu3MCbk',
    published: 'Sep 6, 2023'
  },
];

const Projects = () => {
  return (
    <Box sx={{ py: { xs: 2, md: 4 } }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Projects
      </Typography>
      
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Grid container spacing={4}>
          {projects.map((project, index) => (
            <Grid key={index} sx={{ gridColumn: { xs: 'span 12', md: 'span 6' } }}>
              <MotionCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  backgroundColor: 'background.paper',
                  borderRadius: 2,
                  p: { xs: 2, md: 3 },
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={project.image}
                  alt={project.title}
                  sx={{
                    objectFit: project.imageObjectFit ?? 'cover',
                    backgroundColor: project.imageObjectFit === 'contain' ? 'grey.900' : undefined,
                  }}
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    {project.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Published: {project.published}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {project.description}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {project.technologies.map((tech, i) => (
                      <Chip
                        key={i}
                        label={tech}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </CardContent>
                <CardActions>
                  {project.githubUrl && (
                    <Button
                      size="small"
                      color="primary"
                      href={project.githubUrl}
                      target="_blank"
                      startIcon={<GitHubIcon />}
                    >
                      GitHub
                    </Button>
                  )}
                  {project.videoUrl && (
                    <Button
                      size="small"
                      color="primary"
                      href={project.videoUrl}
                      target="_blank"
                      startIcon={<YouTubeIcon />}
                    >
                      YouTube
                    </Button>
                  )}
                  {project.liveUrl && (
                    <Button
                      size="small"
                      color="primary"
                      href={project.liveUrl}
                      target="_blank"
                      startIcon={<LaunchIcon />}
                    >
                      Live
                    </Button>
                  )}
                </CardActions>
              </MotionCard>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default Projects; 